import { createModel } from '@rematch/core';
import produce from 'immer';

import {
    decodeMethodResult,
    getiZiSwapPoolContract,
    getMiningOneSideiZiTimestampV1Contract,
    getMiningOneSideiZiV1Contract,
    getMulticallContract,
    getVeiZiContract,
} from '../../../../../utils/contractHelpers';
import {
    getWorthOfToken,
    findPoolEntryByPoolKey,
    getPoolAPR,
    addIZIBoostAPR,
    getPriceOracleAByB,
    getPriceOracleAByBDecimal,
    getPoolAPRTimestamp,
} from './funcs';
import { isAddress } from 'ethers/lib/utils';

import {
    MiningContractInfo,
    MiningOneSideMetaInfo,
    RewardInfos,
} from './miningOneSideContractHelper';

import { RootModel } from '../../..';

import {
    FarmOneSideState,
    FarmControl,
    InitPoolListDataParams,
    InitPoolListMetaParams,
    InitPositionParams,

    PoolEntryState,
    MiningPoolData,
    MiningPoolMeta,
    PositionDetail,
    PositionEntry,
    TokenStatus,
    RefreshPoolListDataAndPositionParams,
} from './types';

import { getPositionPoolKey } from '../../../common/positionPoolHelper';

import { parallelCollect } from '../../../../../net/contractCall/parallel';
import { MiningPoolUserData } from './types';

import BigNumber from 'bignumber.js';
import { tokenAddr2Token, tokenSymbol2token } from '../../../../../config/tokens';
import { amount2Decimal } from '../../../../../utils/tokenMath';
import { ChainId, FarmOneSideiZiContractVersion, TokenSymbol } from '../../../../../types/mod';
import { getChain } from '../../../../../config/chains';

import { VEIZI_ADDRESS } from '../../../../../config/veizi/veiziContracts';

import { StateResponse } from '../../../../../types/abis/iZiSwap/Pool';
import { point2PriceDecimal, point2PriceUndecimal } from '../price';
import { getLiquidityManagerContract } from '../../../../../utils/contractFactory';
import { getLiquidityValue } from '../liquidity';

import { Contract } from 'web3-eth-contract';
import { MULTICALL_ADDRESS } from '../../../../../config/multicall/multicallContracts';
import { GetOraclePriceResponse } from '../../../../../types/abis/farm/iZiSwap/oneSide/OneSide';
import { LIQUIDITY_MANAGER_ADDRESS } from '../../../../../config/trade/tradeContracts';


export const farmOneSideiZi = createModel<RootModel>()({
    state: {
        positionManagerContract: undefined,
        poolEntryList: [],
        farmView: {
            isFarmDataLoading: false,
            isUserDataLoading: false,
        },
        farmControl: {
            sortBy: undefined,
            stakedOnly: false,
            searchKey: undefined,
            type: 'live',
        }
    } as FarmOneSideState,
    reducers: {
        setFarmState: (state: FarmOneSideState, payload: FarmOneSideState) => {
            return { ...state, ...payload };
        },
        setFarmControl: (state: FarmOneSideState, farmControl: FarmControl) => produce(state, draft => {
            draft.farmControl = { ...farmControl };
        }),
        setPoolEntryList: (state: FarmOneSideState, payload: PoolEntryState[]) => produce(state, draft => {
            // void freeze object when initPoolListByMeta set meta state first
            draft.poolEntryList = JSON.parse(JSON.stringify(payload));
        }),
        setPoolEntryListMeta: (state: FarmOneSideState, { chainId, poolEntryList }: { chainId: ChainId, poolEntryList: PoolEntryState[] }) => {
            return { ...state, poolEntryList, currentFarmChainId: chainId };
        },
        setPoolEntryListData: (state: FarmOneSideState, payload: PoolEntryState[]) => produce(state, draft => {
            for (const poolEntry of payload) {
                const draftPoolEntry = findPoolEntryByPoolKey(draft.poolEntryList, poolEntry.meta.positionPoolKey);
                draftPoolEntry.data = poolEntry.data;
            }
        }),
        setPositionData: (state: FarmOneSideState, payload: PoolEntryState[]) => produce(state, draft => {
            const positionPoolKeySet = new Set();
            for (const poolEntry of payload) {
                const draftPoolEntry = findPoolEntryByPoolKey(draft.poolEntryList, poolEntry.meta.positionPoolKey);
                draftPoolEntry.userData = poolEntry.userData;
                draftPoolEntry.positionList = poolEntry.positionList;
                draftPoolEntry.stakedPositionList = poolEntry.stakedPositionList;

                positionPoolKeySet.add(poolEntry.meta.positionPoolKey);
            }
            // clean other
            draft.poolEntryList.filter(p => !positionPoolKeySet.has(p.meta.positionPoolKey)).forEach(p => {
                if (p.meta.twoRewards) {
                    p.userData.earned = ['0', '0'];
                } else {
                    p.userData.earned = ['0'];
                }
                p.positionList = [];
                p.stakedPositionList = [];
            });
        }),
        setFarmDataLoading: (state: FarmOneSideState, isLoading: boolean) => produce(state, draft => {
            draft.farmView.isFarmDataLoading = isLoading;
        }),
        setUserDataLoading: (state: FarmOneSideState, isLoading: boolean) => produce(state, draft => {
            draft.farmView.isUserDataLoading = isLoading;
        }),
        togglePoolMetaInitialToggle: (state: FarmOneSideState, positionPoolKey: string) => produce(state, draft => {
            const draftPoolEntry = findPoolEntryByPoolKey(draft.poolEntryList, positionPoolKey);
            draftPoolEntry.meta.initialToggle = !draftPoolEntry.meta.initialToggle;
        }),
    },
    effects: (dispatch) => ({
        async initPoolListMeta(initPoolListMetaParams: InitPoolListMetaParams): Promise<void> {
            const { chainId, metaList } = initPoolListMetaParams;
            if (!chainId) { return; }
            const startTime = new Date();

            const poolEntryList = [] as PoolEntryState[];
            // TODO filter
            // TODO contract multicall or web3 batch request or parallel
            for (const configMeta of metaList ?? []) {
                const data = {} as MiningPoolData;
                const contractVersion = (configMeta.contractVersion) ? configMeta.contractVersion : FarmOneSideiZiContractVersion.V1;
                const additionalKey = configMeta.additionalKey ?? '01';

                const positionPoolKey = getPositionPoolKey(configMeta.tokenA.address, configMeta.tokenB.address, configMeta.feeTier, contractVersion, additionalKey);

                const useTimestamp = configMeta.useTimestamp ?? false

                const meta = { ...configMeta, positionPoolKey, contractVersion, useTimestamp } as MiningPoolMeta;

                const userData = {} as MiningPoolUserData;
                if (meta.twoRewards) {
                    userData.earned = ['0', '0'];
                } else {
                    userData.earned = ['0'];
                }
                const poolEntryState = { meta, data, userData } as PoolEntryState;
                poolEntryList.push(poolEntryState);
            }
            dispatch.farmOneSideiZi.setPoolEntryListMeta({ chainId, poolEntryList });
            // sync init meta data for render basic view
            console.log(`initPoolListMeta end, ${(new Date()).getTime() - startTime.getTime()} ms`);
        },
        async initPoolListData(initPoolListDataParams: InitPoolListDataParams, rootState): Promise<void> {
            const { chainId, web3, positionManagerContract } = initPoolListDataParams;
            if (!chainId || !web3 || !positionManagerContract) { return; }
            if (rootState.farmOneSideiZi.poolEntryList.length === 0) { return; }
            /// check data consistency

            const positionManagerContractExpected = getLiquidityManagerContract(chainId, web3)
            if (
                !positionManagerContractExpected ||
                positionManagerContractExpected.options.address !== positionManagerContract.options.address
            ) { return; }

            const blockDeltaU = getChain(chainId)?.blockDeltaU ?? 10;

            const poolEntryDataList = [] as PoolEntryState[];
            const asyncProcessList: Promise<void>[] = [];
            const baseCalling = [] as string[]
            const baseCallingAddress = [] as string[]
            const baseCallingStart = [] as number[]
            const allMiningContracts = [] as Contract[]
            const allSwapPoolContracts = [] as Contract[]
            for (let poolEntryIdx = 0; poolEntryIdx < rootState.farmOneSideiZi.poolEntryList.length; poolEntryIdx ++) {
                baseCallingStart.push(baseCalling.length)

                const poolEntry = rootState.farmOneSideiZi.poolEntryList[poolEntryIdx]
                const miningContract = poolEntry.meta.useTimestamp ? 
                    getMiningOneSideiZiTimestampV1Contract(poolEntry.meta.miningContract, web3)
                    : getMiningOneSideiZiV1Contract(poolEntry.meta.miningContract, web3)

                allMiningContracts.push(miningContract as Contract)

                const contractInfoCalling = miningContract?.methods.getMiningContractInfo().encodeABI()
                baseCalling.push(contractInfoCalling)
                baseCallingAddress.push(poolEntry.meta.miningContract)

                const oraclePriceCalling = miningContract?.methods.getOraclePrice().encodeABI()
                baseCalling.push(oraclePriceCalling)
                baseCallingAddress.push(poolEntry.meta.miningContract)

                const poolContract = getiZiSwapPoolContract(poolEntry.meta.swapPoolAddress, web3)
                allSwapPoolContracts.push(poolContract as Contract)

                const stateCalling = poolContract?.methods.state().encodeABI()
                baseCalling.push(stateCalling)
                baseCallingAddress.push(poolEntry.meta.swapPoolAddress)

                const rewardInfoNum = poolEntry.meta.twoRewards ? 2:1
                for (let rewardIdx = 0; rewardIdx < rewardInfoNum; rewardIdx ++) {
                    const rewardInfoCalling = miningContract?.methods.rewardInfos(rewardIdx).encodeABI()
                    baseCalling.push(rewardInfoCalling)
                    baseCallingAddress.push(poolEntry.meta.miningContract)
                }
            }

            const multicallContract = getMulticallContract(MULTICALL_ADDRESS[chainId], web3)
            const baseCallingResult = await multicallContract.methods.multicall(baseCallingAddress, baseCalling).call()

            for (let poolEntryIdx = 0; poolEntryIdx < rootState.farmOneSideiZi.poolEntryList.length; poolEntryIdx ++) {
                const poolEntry = rootState.farmOneSideiZi.poolEntryList[poolEntryIdx]
                const { meta } = poolEntry;
                const data = {} as MiningPoolData;
                const poolEntryData = {
                    meta: { positionPoolKey: meta.positionPoolKey } as MiningPoolMeta,
                    data
                } as PoolEntryState;

                poolEntryDataList.push(poolEntryData);

                const asyncProcess = async () => {
                    // 1. get mining contract info
                    const currentCallingStart = baseCallingStart[poolEntryIdx]
                    const miningContractInfo: MiningContractInfo = decodeMethodResult(allMiningContracts[poolEntryIdx], 'getMiningContractInfo', baseCallingResult.results[currentCallingStart])
                    const oraclePrice: GetOraclePriceResponse = decodeMethodResult(allMiningContracts[poolEntryIdx], 'getOraclePrice', baseCallingResult.results[currentCallingStart + 1])
                    const state: StateResponse = decodeMethodResult(allSwapPoolContracts[poolEntryIdx], 'state', baseCallingResult.results[currentCallingStart + 2])
                    const rewardInfos: RewardInfos[] = []

                    const rewardInfoNum = poolEntry.meta.twoRewards ? 2:1
                    
                    for (let rewardIdx = 0; rewardIdx < rewardInfoNum; rewardIdx ++) {
                        const rewardInfo: RewardInfos = decodeMethodResult(allMiningContracts[poolEntryIdx], 'rewardInfos', baseCallingResult.results[currentCallingStart + 3 + rewardIdx])
                        rewardInfos.push(rewardInfo)
                    }


                    const miningOnesideMetaInfo: MiningOneSideMetaInfo = {
                        miningContractInfo: miningContractInfo,
                        oraclePrice,
                        rewardInfos
                    }

                    // 3. get swap and reward token price and set price data
                    const tokenPriceAB = await parallelCollect(
                        dispatch.token.fetchTokenPriceIfMissing(meta.tokenA),
                        dispatch.token.fetchTokenPriceIfMissing(meta.tokenB),
                        dispatch.token.fetchTokenPriceIfMissing(tokenSymbol2token(TokenSymbol.IZI, chainId))
                    );
                    const tokenPriceB: number = tokenPriceAB[1];
                    const tokenPriceA: number = tokenPriceAB[0];
                    const tokenPriceiZi: number = tokenPriceAB[2];

                    data.tokenPriceIZIDecimal = await dispatch.token.fetchTokenPriceIfMissing(tokenSymbol2token(TokenSymbol.IZI, chainId));

                    if (meta.useTimestamp) {
                        const currentTime = new Date().getTime() / 1000
                        data.isEnded = Number(miningOnesideMetaInfo.miningContractInfo.endTime_) < currentTime;
                        data.endTime = Number(miningOnesideMetaInfo.miningContractInfo.endBlock_);
                        data.secondsLeft = data.isEnded ? 0 : Number(miningOnesideMetaInfo.miningContractInfo.endTime_) - currentTime;
                    } else {
                        data.isEnded = Number(miningOnesideMetaInfo.miningContractInfo.endBlock_) < Number(rootState.block.currentBlock);
                        data.endBlock = Number(miningOnesideMetaInfo.miningContractInfo.endBlock_);
                        data.secondsLeft = data.isEnded ? 0 : (Number(miningOnesideMetaInfo.miningContractInfo.endBlock_) - Number(rootState.block.currentBlock)) * blockDeltaU;
                    }

                    if (meta.tokenA.symbol === meta.tokenUni.symbol) {
                        data.tokenPriceUniDecimal = tokenPriceA;
                        data.tokenPriceLockDecimal = tokenPriceB;
                    } else {
                        data.tokenPriceUniDecimal = tokenPriceB;
                        data.tokenPriceLockDecimal = tokenPriceA;
                    }

                    data.tokenPriceADecimal = tokenPriceA;
                    data.tokenPriceBDecimal = tokenPriceB;

                    data.vLiquidity = Number(miningOnesideMetaInfo.miningContractInfo.totalVLiquidity_) * 1e6;
                    data.lockBoostMultiplier = Number(miningOnesideMetaInfo.miningContractInfo.lockBoostMultiplier_);
                    data.totalNIZI = Number(miningOnesideMetaInfo.miningContractInfo.totalNIZI_ ?? 0);
                    data.totalValidVeiZi = Number(miningOnesideMetaInfo.miningContractInfo.totalValidVeiZi_ ?? 0);

                    data.vLiquidityWorth = (meta.tokenA.symbol === meta.tokenLock.symbol) ?
                        getWorthOfToken(meta.tokenB, data.vLiquidity, tokenPriceB)
                        :
                        getWorthOfToken(meta.tokenA, data.vLiquidity, tokenPriceA);
                    data.tokenUniWorth = data.vLiquidityWorth / data.lockBoostMultiplier;
                    data.capital = data.tokenUniWorth + data.vLiquidityWorth;
                    data.tvl = data.capital + getWorthOfToken(
                        tokenSymbol2token(TokenSymbol.IZI, chainId),
                        data.totalNIZI,
                        tokenPriceiZi,
                    );

                    let apr = 0;
                    data.reward = [];
                    data.rewardTokens = [];
                    data.rewardTokenPrice = [];
                    for (const rewardInfo of miningOnesideMetaInfo.rewardInfos) {
                        const rewardToken = tokenAddr2Token(rewardInfo.rewardToken, chainId);

                        const rewardTokenPrice = await dispatch.token.fetchTokenPriceIfMissing(rewardToken) ?? 0;
                        if (data.vLiquidityWorth > 0) {
                            apr += meta.useTimestamp ?
                             getPoolAPRTimestamp(rewardInfo.rewardPerSecond as string, data.capital, { left: rewardToken, right: rewardTokenPrice })
                             :getPoolAPR(rewardInfo.rewardPerBlock as string, chainId, data.capital, { left: rewardToken, right: rewardTokenPrice });
                        }

                        const rewardDecimal = 
                            meta.useTimestamp ?
                            (amount2Decimal(
                                new BigNumber(Number(rewardInfo.rewardPerSecond)),
                                rewardToken
                            ) ?? 0)
                            :(amount2Decimal(
                                new BigNumber(Number(rewardInfo.rewardPerBlock)),
                                rewardToken
                            ) ?? 0);
                        data.reward.push([rewardToken, String(rewardDecimal)]);
                        data.rewardTokens.push(rewardToken);
                        data.rewardTokenPrice.push(rewardTokenPrice);
                    }

                    if (meta.iZiBoost || meta.veiZiBoost) {
                        data.apr = [apr / 2.5, apr];
                    } else {
                        data.apr = [apr];
                    }

                    // 4. set position data
                    data.positionPoolContract = meta.swapPoolAddress;
                    data.positionTick = Number(state.currentPoint);
                    data.currentTick = Number(state.currentPoint);
                    data.currentLiquidity = state.liquidity;
                    data.currentLiquidityX = state.liquidityX;
                    data.priceAByBDecimal = point2PriceDecimal(meta.tokenA, meta.tokenB, data.currentTick);
                    data.priceAByB = Number(point2PriceUndecimal(meta.tokenA, meta.tokenB, data.currentTick));

                    data.oraclePriceAByB = getPriceOracleAByB(miningOnesideMetaInfo.oraclePrice.avgSqrtPriceX96);
                    data.oraclePriceAByBDecimal = getPriceOracleAByBDecimal(chainId, meta.tokenA, meta.tokenB, miningOnesideMetaInfo.oraclePrice.avgSqrtPriceX96);
                    console.log('avg tick: ', miningOnesideMetaInfo.oraclePrice.avgPoint);
                    data.oracleTick = Number(miningOnesideMetaInfo.oraclePrice.avgPoint);
                    console.log('data oracle tick: ', data.oracleTick);

                };
                asyncProcessList.push(asyncProcess());
            }
            await Promise.all(asyncProcessList);

            dispatch.farmOneSideiZi.setPoolEntryListData(poolEntryDataList);
            // .finally(() => dispatch.farm.setFarmDataLoading(false));
        },
        async initPoolList(initPoolListParams: InitPoolListMetaParams & InitPoolListDataParams): Promise<void> {
            await dispatch.farmOneSideiZi.initPoolListMeta(initPoolListParams);
            await dispatch.farmOneSideiZi.initPoolListData(initPoolListParams);
        },
        async initPosition(initPositionParams: InitPositionParams, rootState): Promise<void> {

            const { chainId, web3, positionManagerContract, account } = initPositionParams;

            if (!chainId || !web3 || !account || !positionManagerContract || !rootState.farmOneSideiZi.poolEntryList) { return; }
            if (rootState.farmOneSideiZi.poolEntryList.length === 0) { return; }

              /// check data consistency
            const positionManagerContractExpected = getLiquidityManagerContract(chainId, web3);
            if (
                !positionManagerContractExpected ||
                positionManagerContractExpected.options.address !== positionManagerContract.options.address
            ) { return; }

            const baseCalling = [] as string[]
            const baseCallingAddress = [] as string[]
            const allMiningContracts = [] as Contract[]

            for (let poolEntryIdx = 0; poolEntryIdx < rootState.farmOneSideiZi.poolEntryList.length; poolEntryIdx ++) {
                const poolEntry = rootState.farmOneSideiZi.poolEntryList[poolEntryIdx]

                const miningContract = getMiningOneSideiZiV1Contract(poolEntry.meta.miningContract, web3)
                allMiningContracts.push(miningContract as Contract)

                const pendingRewardsCalling = miningContract?.methods.pendingRewards(account).encodeABI()
                baseCalling.push(pendingRewardsCalling)
                baseCallingAddress.push(poolEntry.meta.miningContract)

                const tokenIdsCalling = miningContract?.methods.getTokenIds(account).encodeABI()
                baseCalling.push(tokenIdsCalling)
                baseCallingAddress.push(poolEntry.meta.miningContract)
            }

            const veiZiAddress = VEIZI_ADDRESS[chainId];
            const veiZiContract = getVeiZiContract(veiZiAddress, web3);
            if (veiZiContract) {
                const stakingInfoCalling = veiZiContract?.methods.stakingInfo(account).encodeABI()
                baseCalling.push(stakingInfoCalling)
                baseCallingAddress.push(veiZiAddress)
            }

            const multicallContract = getMulticallContract(MULTICALL_ADDRESS[chainId], web3)
            const baseCallingResult = await multicallContract.methods.multicall(baseCallingAddress, baseCalling).call()

            const stakingInfo = veiZiContract ? decodeMethodResult(veiZiContract, 'stakingInfo', baseCallingResult.results[baseCallingResult.length - 1]) : undefined

            const veiZi = Number(stakingInfo?.amount ?? 0);
            const veiZiNftId = stakingInfo?.nftId ?? '0';

            const veiZiDecimal = amount2Decimal(new BigNumber(stakingInfo?.amount ?? 0), tokenSymbol2token(TokenSymbol.IZI, chainId)) ?? 0;

            const allPoolEarnedList = [] as string[][]
            const allPoolTokenIds = [] as string[][]

            const tokenId2Idx = new Map<string, number>()
            const tokenId2PoolIdx = new Map<string, number>()
            const allTokenId = [] as string[]

            for (let poolEntryIdx = 0; poolEntryIdx < rootState.farmOneSideiZi.poolEntryList.length; poolEntryIdx ++) {
                const miningContract = allMiningContracts[poolEntryIdx]
                const poolEarnedList = decodeMethodResult(miningContract, 'pendingRewards', baseCallingResult.results[poolEntryIdx * 2])
                allPoolEarnedList.push(poolEarnedList)
                const tokenIds = decodeMethodResult(miningContract, 'getTokenIds', baseCallingResult.results[poolEntryIdx * 2 + 1])
                allPoolTokenIds.push(tokenIds)
                for (const tokenId of tokenIds) {
                    tokenId2Idx.set(tokenId, allTokenId.length)
                    tokenId2PoolIdx.set(tokenId, poolEntryIdx)
                    allTokenId.push(tokenId)
                }
            }

            const secondCalling = [] as string[]
            const secondCallingAddress = [] as string[]

            for (const tokenId of allTokenId) {
                const poolIdx = tokenId2PoolIdx.get(tokenId) as number
                const miningContract = allMiningContracts[poolIdx]
                const tokenStatusCalling = miningContract.methods.tokenStatus(tokenId).encodeABI()
                secondCalling.push(tokenStatusCalling)
                const poolEntry = rootState.farmOneSideiZi.poolEntryList[poolIdx]
                secondCallingAddress.push(poolEntry.meta.miningContract)
            }

            for (const tokenId of allTokenId) {
                const liquiditiesCalling = positionManagerContract.methods.liquidities(tokenId).encodeABI()
                secondCalling.push(liquiditiesCalling)
                secondCallingAddress.push(LIQUIDITY_MANAGER_ADDRESS[chainId])
            }

            const tokenId2PendingRewardSecondCallingIdx = new Map<string, number>()

            for (const tokenId of allTokenId) {
                const poolIdx = tokenId2PoolIdx.get(tokenId) as number
                const miningContract = allMiningContracts[poolIdx]
                const poolEntry = rootState.farmOneSideiZi.poolEntryList[poolIdx]
                if (poolEntry.meta.contractVersion === FarmOneSideiZiContractVersion.V1) {
                    const pendingRewardCalling = miningContract.methods.pendingReward(tokenId).encodeABI()
                    tokenId2PendingRewardSecondCallingIdx.set(tokenId, secondCalling.length)
                    secondCalling.push(pendingRewardCalling)
                    secondCallingAddress.push(poolEntry.meta.miningContract)
                }
            }
            const secondCallingResult = await multicallContract.methods.multicall(secondCallingAddress, secondCalling).call()

            const stakedTokenIdList: string[] = [];
            const stakedTokenStatusList: TokenStatus[] = [];

            type miningPoolValueType = { tokenIdSet: Set<string>, address: string, poolEntry: PoolEntryState }
            const miningPoolData = {} as { [index: string]: miningPoolValueType };

            // get staked tokenIds and corresponding tokenStatus

            for (let poolEntryIdx = 0; poolEntryIdx < rootState.farmOneSideiZi.poolEntryList.length; poolEntryIdx ++) {
                const poolEntryState = rootState.farmOneSideiZi.poolEntryList[poolEntryIdx]

                const positionPoolKey = poolEntryState.meta.positionPoolKey;

                const miningContract = allMiningContracts[poolEntryIdx]

                const poolEarnedList = allPoolEarnedList[poolEntryIdx]

                const tokenIds = allPoolTokenIds[poolEntryIdx]

                const userData = {} as MiningPoolUserData;
                userData.earned = poolEarnedList;
                userData.veiZi = veiZi;
                userData.veiZiNftId = veiZiNftId;
                userData.veiZiDecimal = veiZiDecimal;
                userData.vLiquidity = 0;
                userData.capital = 0;

                const twoRewards = poolEntryState.meta.twoRewards;
                const contractVersion = poolEntryState.meta.contractVersion;
                const iZiBoost = poolEntryState.meta.iZiBoost;

                const poolEntry = {
                    meta: { positionPoolKey, twoRewards, contractVersion, iZiBoost } as MiningPoolMeta,
                    userData: userData,
                    positionList: [] as PositionEntry[],
                    stakedPositionList: [] as PositionEntry[],
                } as PoolEntryState;

                const tokenStatus: TokenStatus[] = []

                for (const tokenId of tokenIds) {
                    const tokenIdx = tokenId2Idx.get(tokenId) as number
                    const tokenStatusResult = decodeMethodResult(miningContract, 'tokenStatus', secondCallingResult.results[tokenIdx])
                    tokenStatus.push(tokenStatusResult)
                }

                miningPoolData[poolEntryState.meta.miningContract] = { tokenIdSet: new Set(tokenIds), address: poolEntryState.meta.miningContract, poolEntry };

                stakedTokenIdList.push(...tokenIds);
                stakedTokenStatusList.push(...tokenStatus);
            }

            // we do not consider unstaked token now

            // const allTokenId = [...stakedTokenIdList];
            const allTokenStatus = [...stakedTokenStatusList];
            const positionResult: string[] = secondCallingResult.results.slice(allTokenId.length, allTokenId.length * 2)
            const positions: PositionDetail[] = positionResult.map((p, i) => {
                const position: PositionDetail = decodeMethodResult(positionManagerContract, 'liquidities', p);
                position.tokenId = allTokenId[i];
                position.tokenStatus = allTokenStatus[i];
                return position;
            });

            const stakedTokenIdSet = new Set(stakedTokenIdList);
            const stakedPositionList = positions.filter(p => stakedTokenIdSet.has(p.tokenId));

            // get pendingReward for each stakedPosition
            for (const stakedPosition of stakedPositionList) {
                const stakedTokenId = stakedPosition.tokenId;
                const miningPool = Object.values(miningPoolData).filter(d => d.tokenIdSet.has(stakedTokenId))[0];
                if (miningPool.poolEntry.meta.contractVersion === FarmOneSideiZiContractVersion.V1) {
                    const pendingRewardCallingIdx = tokenId2PendingRewardSecondCallingIdx.get(stakedTokenId) as number
                    const poolIdx = tokenId2PoolIdx.get(stakedTokenId) as number
                    const miningContract = allMiningContracts[poolIdx]
                    stakedPosition.earned = decodeMethodResult(miningContract, 'pendingReward', secondCallingResult.results[pendingRewardCallingIdx])
                }
            }

            // pure function calculate data
            for (const position of positions) {

                const isStaked = (position.tokenStatus !== undefined);

                if (!isStaked) {
                    continue;
                }

                // let miningPool: miningPoolValueType;
                const miningPool = Object.values(miningPoolData).find(d => d.tokenIdSet.has(position.tokenId.toString())) as miningPoolValueType;

                const { meta, data } = rootState.farmOneSideiZi.poolEntryList.find(p => p.meta.miningContract === miningPool.address) as PoolEntryState;
                const poolEntry = miningPool.poolEntry;
                const [tokenPriceA, tokenPriceB, tokenPriceiZi] = await parallelCollect(
                    dispatch.token.fetchTokenPriceIfMissing(meta.tokenA),
                    dispatch.token.fetchTokenPriceIfMissing(meta.tokenB),
                    dispatch.token.fetchTokenPriceIfMissing(tokenSymbol2token(TokenSymbol.IZI, chainId)),
                );
                // let amountLock = 0;
                const positionEntry = { nftId: position.tokenId, isStaked } as PositionEntry;

                positionEntry.vLiquidity = Number(position.tokenStatus.vLiquidity) * 1e6;
                const amountLock = new BigNumber(position.tokenStatus.lockAmount);
                positionEntry.amountLockDecimal = Number(amount2Decimal(amountLock, meta.tokenLock));


                positionEntry.vLiquidityWorth = getWorthOfToken(
                    (meta.tokenLock.symbol === meta.tokenA.symbol ? meta.tokenB : meta.tokenA),
                    positionEntry.vLiquidity,
                    (meta.tokenLock.symbol === meta.tokenA.symbol ? tokenPriceB : tokenPriceA),
                );

                positionEntry.amountUniWorth = positionEntry.vLiquidityWorth / data.lockBoostMultiplier;

                positionEntry.capital = positionEntry.amountUniWorth + positionEntry.vLiquidityWorth;
                positionEntry.tvl = positionEntry.capital + getWorthOfToken(
                    tokenSymbol2token(TokenSymbol.IZI, chainId),
                    Number(position.tokenStatus.nIZI ?? 0),
                    tokenPriceiZi,
                );

                positionEntry.liquidity = position.liquidity;
                positionEntry.tickLower = position.leftPt;
                positionEntry.tickUpper = position.rightPt;

                const lockPriceDecimal = (meta.tokenLock.symbol === meta.tokenA.symbol ? tokenPriceA : tokenPriceB);
                positionEntry.amountLockWorth = lockPriceDecimal * positionEntry.amountLockDecimal;

                const priceLockByUni0 = Number(point2PriceUndecimal(meta.tokenLock, meta.tokenUni, Number(position.leftPt)))
                const priceLockByUni1 = Number(point2PriceUndecimal(meta.tokenLock, meta.tokenUni, Number(position.rightPt)))
                positionEntry.minPrice = Math.min(priceLockByUni0, priceLockByUni1);
                positionEntry.maxPrice = Math.max(priceLockByUni0, priceLockByUni1);
                const priceDecimalLockByUni0 = point2PriceDecimal(meta.tokenLock, meta.tokenUni, Number(position.leftPt))
                const priceDecimalLockByUni1 = point2PriceDecimal(meta.tokenLock, meta.tokenUni, Number(position.rightPt))
                positionEntry.minPriceDecimal = Math.min(priceDecimalLockByUni0, priceDecimalLockByUni1);
                positionEntry.maxPriceDecimal = Math.max(priceDecimalLockByUni0, priceDecimalLockByUni1);

                positionEntry.earnedDecimal = [];
                positionEntry.earnedWorth = [];

                positionEntry.earned = [];
                if (poolEntry.meta.contractVersion === FarmOneSideiZiContractVersion.V1) {
                    for (const idx in data.rewardTokens) {
                        const rewardToken = data.rewardTokens[idx];
                        const earnedDecimal = amount2Decimal(new BigNumber(position.earned[idx]), rewardToken);
                        positionEntry.earned.push(Number(position.earned[idx]));
                        positionEntry.earnedDecimal.push(Number(earnedDecimal));
                        const rewardTokenPrice = data.rewardTokenPrice[idx];
                        const earnedWorth: number = getWorthOfToken(rewardToken, Number(position.earned[idx]), rewardTokenPrice);
                        positionEntry.earnedWorth.push(earnedWorth);
                    }
                }

                positionEntry.niZi = Number(position.tokenStatus.nIZI ?? 0);
                positionEntry.niZiDecimal = Number(amount2Decimal(new BigNumber(positionEntry.niZi), tokenSymbol2token(TokenSymbol.IZI, chainId)));

                if (positionEntry.vLiquidity > 0) {
                    if (meta.contractVersion === FarmOneSideiZiContractVersion.V1) {
                        positionEntry.apr = addIZIBoostAPR({ data, meta } as PoolEntryState, chainId, positionEntry.vLiquidity, positionEntry.capital, positionEntry.niZi, 0);
                    } else {
                        positionEntry.apr = 0;
                    }
                } else {
                    positionEntry.apr = 0;
                }

                const {amountX, amountY} = getLiquidityValue(positionEntry.liquidity, Number(positionEntry.tickLower), Number(positionEntry.tickUpper), data.currentTick, data.currentLiquidity, data.currentLiquidityX)
                positionEntry.amountAInUniswapDecimal = Number(amount2Decimal(new BigNumber(amountX), meta.tokenA));
                positionEntry.amountBInUniswapDecimal = Number(amount2Decimal(new BigNumber(amountY), meta.tokenB));
                positionEntry.worthAInUniswap = positionEntry.amountAInUniswapDecimal * tokenPriceA
                positionEntry.worthBInUniswap = positionEntry.amountBInUniswapDecimal * tokenPriceB


                // const [fee0InUniswap, fee1InUniswap] = get
                poolEntry.stakedPositionList.push(positionEntry);
                poolEntry.userData.capital += positionEntry.capital;
                poolEntry.userData.vLiquidity += positionEntry.vLiquidity;

            }

            // TODO save positions and positionManagerContract address
            dispatch.farmOneSideiZi.setPositionData(Object.values(miningPoolData).map(m => m.poolEntry));
        },
        async refreshPoolListDataAndPosition(refreshPoolListDataAndPositionParams: RefreshPoolListDataAndPositionParams): Promise<void> {
            await dispatch.farmOneSideiZi.initPoolListData(refreshPoolListDataAndPositionParams as InitPoolListDataParams);
            await dispatch.farmOneSideiZi.initPosition(refreshPoolListDataAndPositionParams as InitPositionParams);
        },
        async cleanPositionIfExist(account: string | null | undefined, rootState): Promise<void> {
            if (!isAddress(account as string)) { return; }
            if (!rootState.farmOneSideiZi.poolEntryList.find(p => p.positionList?.length || p.stakedPositionList?.length)) { return; }
            // clean user data
            dispatch.farmOneSideiZi.setPositionData([]);
            console.info('cleanPosition end');
        },
    })
});
